import { Box, Grid, Skeleton, Stack } from '@mui/material';
import React from 'react';
import { IDataprevEmployee } from 'services/zetra';
import { formatCpfOrCnpj, formatDateFromNumberString, maskCurrencyInCents } from 'helpers';
import { EmployeeCard, EmployerCard } from './Cards';

type DataPrevMarginQueryListProps = {
    queryData: IDataprevEmployee[];
    updateQueryData?: () => void;
    isLoading?: boolean;
};

export const DataPrevMarginQueryList: React.FC<DataPrevMarginQueryListProps> = ({
    queryData,
    updateQueryData,
    isLoading = false
}) => {
    const employee = queryData[0];

    const personalFields = [
        {
            label: 'Nome',
            value: employee?.nome || 'N/A'
        },
        {
            label: 'CPF',
            value: formatCpfOrCnpj(String(employee?.cpf)) || 'N/A'
        },
        {
            label: 'Data de Nascimento',
            value: formatDateFromNumberString(employee?.dataNascimento) || 'N/A'
        },
        {
            label: 'Nome da Mãe',
            value: employee?.nomeMae || 'N/A'
        },
        {
            label: 'PEP',
            value: employee?.pessoaExpostaPoliticamente
                ? employee.pessoaExpostaPoliticamente.codigo === 0
                    ? 'Não'
                    : 'Sim'
                : 'N/A'
        }
    ];

    const getEmployerFields = (emp: IDataprevEmployee) => [
        {
            label: 'Início de atividades',
            value: formatDateFromNumberString(emp?.dataInicioAtividadeEmpregador) || 'N/A'
        },
        {
            label: 'CNAE',
            value: emp?.cnae?.descricao || 'N/A'
        },
        {
            label: 'Número do CNAE',
            value: emp?.cnae?.codigo || 'N/A'
        },
        {
            label: 'Cargo',
            value: emp?.cbo?.descricao || 'N/A'
        },
        {
            label: 'Data de Admissão',
            value: formatDateFromNumberString(emp?.dataAdmissao) || 'N/A'
        },
        {
            label: 'Salário bruto',
            value: maskCurrencyInCents(emp?.valorTotalVencimentos) || 'N/A'
        },
        {
            label: 'Salário líquido',
            value: maskCurrencyInCents(emp?.valorBaseMargem) || 'N/A'
        },
        {
            label: 'Elegível',
            value: emp?.elegivel ? 'Sim' : 'Não'
        },
        {
            label: 'Motivo da Inelegibilidade',
            value: emp?.motivoInelegibilidade || 'N/A'
        },
        {
            label: 'Empréstimos ativos',
            value: emp?.qtdEmprestimosAtivosSuspensos || 'Nenhum'
        }
    ];

    const LoadingCards: React.FC = () => (
        <Stack spacing={2}>
            <Box
                sx={{
                    width: '100%',
                    p: 5,
                    borderRadius: 2,
                    backgroundColor: '#F4F3F3',
                    mb: 2
                }}
            >
                <Skeleton variant="text" width={180} height={28} sx={{ mb: 2, borderRadius: 1 }} />
                <Grid container spacing={0} alignItems="stretch">
                    {[...Array(5)].map((_, idx) => (
                        <Grid item xs={12} sm={6} md={2.4} key={idx}>
                            <Skeleton
                                variant="text"
                                width="100%"
                                height={20}
                                sx={{ mb: 1, borderRadius: 1 }}
                            />
                            <Skeleton
                                variant="text"
                                width="80%"
                                height={20}
                                sx={{ borderRadius: 1 }}
                            />
                        </Grid>
                    ))}
                </Grid>
            </Box>
            {[...Array(2)].map((_, idx) => (
                <Box
                    key={idx}
                    sx={{
                        width: '100%',
                        p: 5,
                        borderRadius: 2,
                        backgroundColor: '#F4F3F3',
                        mb: 2
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            justifyContent: 'space-between',
                            alignItems: { xs: 'flex-start', sm: 'center' },
                            mb: 2
                        }}
                    >
                        <Skeleton
                            variant="text"
                            width={300}
                            height={28}
                            sx={{ mb: 1, borderRadius: 1 }}
                        />
                        <Skeleton
                            variant="rectangular"
                            width={180}
                            height={40}
                            sx={{
                                borderRadius: '360px',
                                backgroundColor: '#E0E0E0'
                            }}
                        />
                    </Box>
                    <Grid container spacing={0} alignItems="stretch">
                        {[...Array(10)].map((_, idx) => (
                            <Grid item xs={12} sm={6} md={2.4} key={idx}>
                                <Skeleton
                                    variant="text"
                                    width="100%"
                                    height={20}
                                    sx={{ mb: 1, borderRadius: 1 }}
                                />
                                <Skeleton
                                    variant="text"
                                    width="80%"
                                    height={20}
                                    sx={{ borderRadius: 1 }}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            ))}
        </Stack>
    );

    return (
        <Stack spacing={2}>
            {isLoading ? (
                <LoadingCards />
            ) : (
                <React.Fragment>
                    <EmployeeCard headerData={employee} fields={personalFields} />
                    {queryData?.map((emp, idx) => (
                        <EmployerCard
                            key={idx}
                            headerData={emp}
                            fields={getEmployerFields(emp)}
                            updateQueryData={updateQueryData}
                        />
                    ))}
                </React.Fragment>
            )}
        </Stack>
    );
};
